import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Disclosure,
  DisclosureContent,
  DisclosureTrigger,
  Field,
  Flex,
  Text,
  Textarea,
} from '@optiaxiom/react';
import { IconArrowRight, IconPlay, IconTrashCan, IconXmark } from '@optimizely/axiom-icons';

import { MarkdownText } from '@frontend/components/MarkdownText';
import { NamespacedLink } from '@frontend/components/ui';
import {
  useGetWorkflowAgentExecution,
  useTriggerWorkflowAgent,
} from '@frontend/features/agents/hooks';
import { WorkflowAgent } from '@frontend/types/workflow-agent';

import { styles } from './WorkflowAgentExecutionForm.css';

type WorkflowAgentExecutionFormProps = {
  agent?: WorkflowAgent;
  hideRightSidebar: () => void;
};

function useCountdownTimer(isActive: boolean, duration: number = 3) {
  const [countdown, setCountdown] = useState(duration);

  useEffect(() => {
    if (!isActive) {
      setCountdown(duration);
      return;
    }

    setCountdown(duration);
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          return duration; // Reset to full duration
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isActive, duration]);

  return countdown;
}

export function WorkflowAgentExecutionForm({
  agent,
  hideRightSidebar,
}: WorkflowAgentExecutionFormProps) {
  const [inputText, setInputText] = useState<string>('');
  const [executionId, setExecutionId] = useState<string | undefined>(undefined);

  const { data: executionData, isFetching: isFetchingExecution } = useGetWorkflowAgentExecution(
    executionId || undefined
  );

  const isPendingExecution = executionData?.status === 'pending';
  const countdown = useCountdownTimer(isPendingExecution && !isFetchingExecution);

  const { mutate: triggerAgent, isPending: isTriggering } = useTriggerWorkflowAgent(data => {
    setExecutionId(data.id);
  });

  const handleExecuteAgent = async () => {
    if (!agent || !agent.id) {
      console.error('No agent selected');
      return;
    }

    if (!inputText.trim()) {
      console.error('Input text is required');
      return;
    }
    setExecutionId(undefined);
    triggerAgent({
      agentId: agent.id,
      inputText: inputText.trim(),
    });
  };

  const clearInput = () => {
    setInputText('');
    setExecutionId(undefined);
  };

  const clearOutput = () => {
    setExecutionId(undefined);
  };

  const isExecuting = isTriggering || isFetchingExecution;

  if (!agent) {
    return (
      <Flex flexDirection="column" gap="16" p="16">
        <Text color="fg.secondary">No workflow agent selected for execution.</Text>
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" gap="16" p="16">
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        className={styles.header}
      >
        <Text fontWeight="600" fontSize="lg">
          Test Run: {agent.name}
        </Text>
        <Button icon={<IconXmark size="16" />} appearance="default" onClick={hideRightSidebar} />
      </Flex>

      {/* Input Disclosure */}
      <Disclosure defaultOpen>
        <DisclosureTrigger>
          <Field label="Input" info="Provide input text to trigger the workflow" />
        </DisclosureTrigger>
        <DisclosureContent>
          <Flex flexDirection="column" gap="16" px="16">
            <Flex flexDirection="column" gap="16">
              <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
                <Text fontSize="sm" color="fg.secondary">
                  Enter text to trigger the workflow agent
                </Text>
                <Button
                  size="sm"
                  appearance="subtle"
                  icon={<IconTrashCan size="14" />}
                  onClick={clearInput}
                >
                  Clear
                </Button>
              </Flex>

              <Field label="Trigger Text" info="Text input for the workflow">
                <Textarea
                  value={inputText}
                  placeholder="Enter your message or trigger text here..."
                  onChange={e => setInputText(e.target.value)}
                  rows={4}
                  resize="vertical"
                />
              </Field>

              {!inputText.trim() && (
                <Text fontSize="sm" color="fg.warning">
                  Input text is required to trigger the workflow
                </Text>
              )}

              {executionData && executionData.status === 'pending' && (
                <Text fontSize="sm" color="fg.secondary">
                  {isFetchingExecution
                    ? `Status: ${executionData.status}, Checking for updates...`
                    : `Status: ${executionData.status}, Refetching in ${countdown} second${countdown !== 1 ? 's' : ''}...`}
                </Text>
              )}

              {executionData && executionData.status === 'failed' && (
                <Text fontSize="sm" color="fg.error">
                  ✗ Execution failed with status: {executionData.error_message}
                </Text>
              )}

              {executionData && executionData.status === 'success' && (
                <Text fontSize="sm" color="fg.accent">
                  ✓ Execution completed with status: {executionData.status}
                </Text>
              )}

              <Button
                appearance="primary"
                icon={<IconPlay size="16" />}
                onClick={handleExecuteAgent}
                loading={isExecuting}
                disabled={isExecuting || !inputText.trim()}
              >
                Run Workflow
              </Button>
            </Flex>
          </Flex>
        </DisclosureContent>
      </Disclosure>

      {/* Output Disclosure */}
      <Disclosure defaultOpen>
        <DisclosureTrigger>
          <Field label="Output" info="Workflow execution results" />
        </DisclosureTrigger>
        <DisclosureContent>
          <Flex flexDirection="column" gap="16" px="16">
            {executionData ? (
              <Flex flexDirection="column" gap="16">
                <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
                  <Text fontSize="sm" color="fg.secondary">
                    Execution Results
                  </Text>
                  <Button
                    size="sm"
                    appearance="subtle"
                    icon={<IconTrashCan size="14" />}
                    onClick={clearOutput}
                  >
                    Clear
                  </Button>
                </Flex>

                <Field label="Execution Details">
                  <Box border="1" borderColor="border.default" rounded="md">
                    <Flex flexDirection="column">
                      <Box bg="bg.secondary" p="12">
                        <Flex flexDirection="column" gap="8" mb="8">
                          <Text fontSize="sm" fontFamily="mono">
                            <strong>Execution ID:</strong> {executionData.id}
                          </Text>
                          <NamespacedLink to={`/agents/executions/${executionData.id}`}>
                            <Flex flexDirection="row" cursor="pointer" alignItems="center" gap="8">
                              <Text color="fg.accent" fontSize="sm">
                                View Full Execution
                              </Text>
                              <IconArrowRight size="12" />
                            </Flex>
                          </NamespacedLink>
                        </Flex>
                        <Text fontSize="sm" fontFamily="mono">
                          <strong>Status:</strong> {executionData.status}
                        </Text>
                        <Text fontSize="sm" fontFamily="mono">
                          <strong>Start Time:</strong> {executionData.start_time}
                        </Text>
                        {executionData.end_time && (
                          <Text fontSize="sm" fontFamily="mono">
                            <strong>End Time:</strong> {executionData.end_time}
                          </Text>
                        )}
                        {executionData.result?.execution_time && (
                          <Text fontSize="sm" fontFamily="mono">
                            <strong>Execution Time:</strong> {executionData.result.execution_time}s
                          </Text>
                        )}
                        {executionData.result?.token_usage && (
                          <Text fontSize="sm" fontFamily="mono">
                            <strong>Token Usage:</strong>{' '}
                            {executionData.result.token_usage.total_tokens} total (
                            {executionData.result.token_usage.input_tokens} input,{' '}
                            {executionData.result.token_usage.output_tokens} output)
                          </Text>
                        )}
                      </Box>
                      {executionData.error_message && (
                        <Box bg="bg.accent.subtle" p="12" borderT="1" borderColor="border.default">
                          <Text fontSize="sm" color="fg.accent">
                            <strong>Error:</strong> {executionData.error_message}
                          </Text>
                        </Box>
                      )}
                      {executionData.result?.response && (
                        <Box bg="bg.default" p="12" border="1" borderColor="border.default">
                          <MarkdownText text={executionData.result.response} />
                        </Box>
                      )}
                      <Box bg="bg.secondary" p="12" borderT="1" borderColor="border.default">
                        <Text fontSize="sm" fontFamily="mono">
                          <strong>Input:</strong> {executionData?.payload?.text_content}
                        </Text>
                      </Box>
                    </Flex>
                  </Box>
                </Field>
              </Flex>
            ) : (
              <Flex
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                py="24"
                gap="8"
                bg="bg.secondary"
                rounded="md"
              >
                <Text color="fg.secondary">No execution results yet.</Text>
                <Text fontSize="sm" color="fg.secondary">
                  Run the workflow to see results here.
                </Text>
              </Flex>
            )}
          </Flex>
        </DisclosureContent>
      </Disclosure>
    </Flex>
  );
}
